import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import * as topojson from 'topojson-client';
import useMapData from '../hooks/useMapData';
import { DISTRICT_COLORS } from '../utils/projection';
import './Map.css';

const Tooltip = ({ data, visible, x, y }) => {
  if (!visible || !data) return null;

  return (
    <div 
      className="map-tooltip" 
      style={{ 
        position: 'absolute', 
        left: x + 20, 
        top: y, 
        pointerEvents: 'none',
        background: 'white',
        padding: '12px',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        zIndex: 1000,
        minWidth: '200px',
        fontFamily: 'sans-serif',
        fontSize: '12px'
      }}
    >
      <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: 'bold' }}>
        Количество ООВО по категориям
      </h4>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#27ae60' }}></div>
          <span>высокий уровень зрелости: 1</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#f1c40f' }}></div>
          <span>средний уровень</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#e74c3c' }}></div>
          <span>низкий уровень</span>
        </div>
      </div>
    </div>
  );
};

function Map({ level, currentRegion, onRegionClick }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, data: null });

  // Always load the full map data
  const { geoData, districtMapping, error } = useMapData('russia');

  // Handle Resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width } = containerRef.current.getBoundingClientRect();
        const mapWidth = width - 32;
        setDimensions({
          width: mapWidth,
          height: mapWidth * 0.65 // Slightly wider than 4:3
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Main D3 Rendering & Logic
  useEffect(() => {
    if (!svgRef.current || !geoData || !districtMapping) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove(); // Clear previous render

    const { width, height } = dimensions;
    const geojson = topojson.feature(geoData, geoData.objects.name);

    // Prepare Data: Map Subjects to Districts
    const subjectToDistrict = {};
    const districtSubjects = {}; // districtId -> [features]

    Object.entries(districtMapping).forEach(([dId, dInfo]) => {
      districtSubjects[dId] = [];
      dInfo.subjects.forEach(subCode => {
        subjectToDistrict[subCode] = { id: dId, name: dInfo.name };
      });
    });

    geojson.features.forEach(f => {
      const iso = f.properties.ISO_2;
      const dist = subjectToDistrict[iso];
      if (dist) {
        f.properties.districtId = dist.id;
        f.properties.districtName = dist.name;
        if (districtSubjects[dist.id]) {
          districtSubjects[dist.id].push(f);
        }
      }
    });

    // Setup Projection
    const projection = d3.geoConicEqualArea()
      .parallels([50, 70])
      .rotate([-100, 0])
      .center([0, 66]);
      
    projection.fitExtent([[20, 20], [width - 20, height - 20]], geojson);
    const pathGenerator = d3.geoPath().projection(projection);

    // Root Group for Zoom/Pan transitions
    const g = svg.append('g');

    // --- 1. Draw All Paths ---
    const paths = g.selectAll('path')
      .data(geojson.features)
      .enter()
      .append('path')
      .attr('d', pathGenerator)
      .attr('stroke', '#fff')
      .attr('stroke-width', 0.5)
      .attr('cursor', 'pointer');

    // --- 2. District Labels (Donuts) ---
    const districtLabelsGroup = g.append('g').attr('class', 'district-labels');
    
    const generateChartData = (seed) => {
        const hash = (seed || '').split('').reduce((a, b) => a + b.charCodeAt(0), 0);
        return [
          { value: 30 + (hash % 30), color: '#27ae60' },
          { value: 20 + ((hash * 2) % 25), color: '#f1c40f' },
          { value: 15 + ((hash * 3) % 20), color: '#e74c3c' }
        ];
    };

    const renderDonut = (parentG, x, y, size, data) => {
        const r = size / 2;
        const pie = d3.pie().value(d => d.value).sort(null);
        const arc = d3.arc().innerRadius(r * 0.6).outerRadius(r);
        
        const dg = parentG.append('g')
            .attr('transform', `translate(${x}, ${y})`)
            .attr('class', 'donut-chart')
            .on('mouseenter', (event) => {
                 const [mx, my] = d3.pointer(event, containerRef.current);
                 setTooltip({ visible: true, x: mx, y: my, data: true });
            })
            .on('mouseleave', () => {
                 setTooltip(prev => ({ ...prev, visible: false }));
            });

        dg.selectAll('path')
            .data(pie(data))
            .enter().append('path')
            .attr('d', arc)
            .attr('fill', d => d.data.color)
            .attr('stroke', '#fff')
            .attr('stroke-width', 1);
        
        return dg;
    };

    Object.entries(districtSubjects).forEach(([dId, features]) => {
        if (!features.length) return;
        const centroids = features.map(f => pathGenerator.centroid(f)).filter(c => !isNaN(c[0]));
        if (!centroids.length) return;
        const cx = d3.mean(centroids, c => c[0]);
        const cy = d3.mean(centroids, c => c[1]);

        const labelG = districtLabelsGroup.append('g')
            .attr('class', 'district-label')
            .attr('data-id', dId)
            .on('click', () => onRegionClick(dId, districtMapping[dId].name));
        
        // Donut
        renderDonut(labelG, cx, cy, 32, generateChartData(dId));
        
        // Text
        const name = districtMapping[dId].name;
        const words = name.split(' ');
        labelG.append('text')
            .attr('x', cx)
            .attr('y', cy + 24)
            .attr('text-anchor', 'middle')
            .attr('font-size', '10px')
            .attr('font-weight', 'bold')
            .attr('fill', '#2c3e50')
            .attr('pointer-events', 'none')
            .text(words[0] + (words.length > 1 ? '...' : '')); 
    });

    // --- 3. Subject Labels (Donuts) ---
    const subjectLabelsGroup = g.append('g').attr('class', 'subject-labels').attr('opacity', 0);
    
    geojson.features.forEach(f => {
        const c = pathGenerator.centroid(f);
        if (isNaN(c[0])) return;
        
        const subG = subjectLabelsGroup.append('g')
            .attr('class', 'subject-label')
            .attr('data-district', f.properties.districtId)
            .on('click', () => onRegionClick(f.properties.ISO_2, f.properties.NAME_1));
            
        renderDonut(subG, c[0], c[1], 24, generateChartData(f.properties.ISO_2));
    });


    // --- State Transitions ---

    const colorScale = d3.scaleOrdinal(d3.schemeSet3);

    if (level === 'russia') {
        // Reset Transform
        g.transition().duration(750).attr('transform', 'translate(0,0)scale(1)');

        // Show All Paths
        paths.transition().duration(750)
            .attr('fill', d => DISTRICT_COLORS[d.properties.districtId] || '#ccc')
            .attr('opacity', 0.8)
            .attr('stroke-width', 0.5);

        // Reset click/hover events for Russia View
        paths.on('click', (e, d) => {
             if (d.properties.districtId) {
                 onRegionClick(d.properties.districtId, d.properties.districtName);
             }
        });
        
        paths.on('mouseenter', function(event, d) {
             const distId = d.properties.districtId;
             if (!distId) return;
             // Highlight all in district
             paths.filter(p => p.properties.districtId === distId)
                  .transition().duration(200)
                  .attr('opacity', 1)
                  .attr('stroke-width', 1.5);
        });
        
        paths.on('mouseleave', function(event, d) {
             const distId = d.properties.districtId;
             if (!distId) return;
             paths.filter(p => p.properties.districtId === distId)
                  .transition().duration(200)
                  .attr('opacity', 0.8)
                  .attr('stroke-width', 0.5);
        });

        // Toggle Labels
        districtLabelsGroup.transition().duration(500).attr('opacity', 1).style('pointer-events', 'all');
        subjectLabelsGroup.transition().duration(500).attr('opacity', 0).style('pointer-events', 'none');

    } else if (level === 'federal-district' && currentRegion) {
        // Find bounds of district
        const distFeatures = geojson.features.filter(f => f.properties.districtId === currentRegion);
        if (distFeatures.length) {
             // Calculate bounds
             const bounds = pathGenerator.bounds({ type: 'FeatureCollection', features: distFeatures });
             const [[x0, y0], [x1, y1]] = bounds;
             const bw = x1 - x0;
             const bh = y1 - y0;
             const scale = 0.9 / Math.max(bw / width, bh / height);
             const tx = width / 2 - scale * (x0 + x1) / 2;
             const ty = height / 2 - scale * (y0 + y1) / 2;

             // Zoom to district
             g.transition().duration(1000)
              .attr('transform', `translate(${tx},${ty})scale(${scale})`);
              
             // Style Paths
             paths.transition().duration(1000)
               .attr('opacity', d => d.properties.districtId === currentRegion ? 0.9 : 0)
               .attr('fill', d => d.properties.districtId === currentRegion ? colorScale(d.properties.ISO_2) : '#eee')
               .attr('stroke-width', d => d.properties.districtId === currentRegion ? 1 / scale : 0); // Scale stroke

             // Update Events for District View
             paths.on('click', null); // Clear previous
             paths.on('mouseenter', null);
             paths.on('mouseleave', null);

             // Re-bind click for subjects
             paths.filter(d => d.properties.districtId === currentRegion)
                  .on('click', (e, d) => onRegionClick(d.properties.ISO_2, d.properties.NAME_1))
                  .on('mouseenter', function() {
                      d3.select(this).transition().duration(200).attr('opacity', 1);
                  })
                  .on('mouseleave', function() {
                      d3.select(this).transition().duration(200).attr('opacity', 0.9);
                  });

             // Toggle Labels
             districtLabelsGroup.transition().duration(500).attr('opacity', 0).style('pointer-events', 'none');
             
             // Show subject labels only for this district
             subjectLabelsGroup.selectAll('.subject-label')
                .attr('opacity', 0) // Hide all first
                .style('pointer-events', 'none');
                
             subjectLabelsGroup.selectAll('.subject-label')
                .filter(function() { return d3.select(this).attr('data-district') === currentRegion; })
                .transition().duration(1000)
                .attr('opacity', 1)
                .style('pointer-events', 'all');
        }
    }

  }, [geoData, districtMapping, dimensions, level, currentRegion]);

  if (error) {
    return <div className="map-error">Ошибка: {error}</div>;
  }

  return (
    <div className="map-container" ref={containerRef}>
      <svg
        ref={svgRef}
        width={dimensions.width}
        height={dimensions.height}
        className="map-svg"
        style={{ background: '#f8f9fa' }}
      />
      <Tooltip {...tooltip} />
      <div className="map-info">
        <p>Уровень: <strong>{level === 'russia' ? 'Страна' : level === 'federal-district' ? 'Федеральный округ' : 'Субъект'}</strong></p>
      </div>
    </div>
  );
}

export default Map;